import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  Grid,
  Card,
  CardBody,
  HStack,
  VStack,
  Icon,
  Divider,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  Tag
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import {  } from '@chakra-ui/icons';
import { fetchWrapper } from '../../../../helpers';
import { BsGrid3X3Gap, BsList } from 'react-icons/bs';
import Pagination from 'react-js-pagination';
import { CHANNELS } from '../../../../utils/channels';

/**
 * One-line human summary of a template body. Email templates store a whole HTML
 * document in `message`, so the card previously rendered visible "<!DOCTYPE html>
 * <html xmlns=…" markup. Drop <head>/<style>/<script> outright (their contents are
 * not copy), then strip remaining tags and collapse whitespace.
 */
const toPlainSummary = (raw) => {
  const s = String(raw || '');
  if (!s) return '';
  return s
    .replace(/<(head|style|script)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
};

export const TemplateSelection = ({ formData, setFormData }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(formData.selectedTemplate || null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const debounceRef = useRef(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_events: 0,
  });

  const loadList = async (page = 1, searchQuery = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        filter_type: formData?.campaignType || 3,
        page,
        limit: pagination.per_page,
        ...(searchQuery ? { search: searchQuery } : {}),
      });
      const url = `${process.env.REACT_APP_API_URL}/template/msg-temp-type-ids?${params}`;
      const data = await fetchWrapper.get(url);

      setTemplates(data.data || []);

      // update pagination state from API response
      setPagination({
        current_page: data.pagination?.page || 1,
        per_page: data.pagination?.limit || 10,
        total_events: data.pagination?.total || 0,
      });
    } catch (err) {
      console.error('Failed to load templates', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    loadList(pageNumber);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      loadList(1, value);
    }, 400);
  };

  useEffect(() => {
    loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.campaignType]);
  // useEffect(() => {
  //   if (templates.length > 0) {
  //     const defaultTemplate = templates[0];
  //     setSelectedTemplate(defaultTemplate);
  //     setFormData(prev => ({
  //       ...prev,
  //       selectedTemplate: defaultTemplate
  //     }));
  //   }
  // }, [templates]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      selectedTemplate: template
    }));
  };

  return (
    <Box className='box-style-card'>
      <Box>
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Text fontSize="xl" fontWeight="semibold" color="gray.800">
              Template Selection
            </Text>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Choose a template for your campaign. You'll select delivery channels in the final step
            </Text>
          </Box>
          
          <Flex align="center" gap={2}>
            <InputGroup  maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={handleSearchChange}
            fontSize="sm"
            bg="white"
          />
        </InputGroup>
          
            
            <Tooltip label={viewMode === 'grid' ? 'Switch to List View' : 'Switch to Grid View'}>
              <IconButton
                size="sm"
                variant="outline"
                icon={viewMode === 'grid' ? <BsList /> : <BsGrid3X3Gap />}
                onClick={() => setViewMode(viewMode === 'grid' ? 'vertical' : 'grid')}
                colorScheme="blue"
              />
            </Tooltip>
          </Flex>
        </Flex>

        

        {/* Templates Grid */}
        <Grid
          templateColumns={viewMode === 'grid' ? 'repeat(3, 1fr)' : '1fr'}
          gap={4}
        >
          {templates.map((template) => (
            <Card
              key={template.id}
              border={selectedTemplate?.id === template.id ? '2px solid var(--brand-primary)' : '1px solid #e2e8f0'}
              bg={selectedTemplate?.id === template.id ? 'blue.50' : 'white'}
              cursor="pointer"
              onClick={() => handleTemplateSelect(template)}
              _hover={{ borderColor: 'var(--brand-primary)', shadow: 'md' }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Flex justify="space-between" align="start" mb={3}>
                  <Text fontWeight="semibold" color="gray.800" fontSize="sm">
                    {template.name}
                  </Text>
                  <Box
                    ml={2}
                    w={4}
                    h={4}
                    border="2px solid"
                    borderColor={selectedTemplate?.id === template.id ? 'var(--brand-primary)' : 'gray.300'}
                    borderRadius="full"
                    bg={selectedTemplate?.id === template.id ? 'var(--brand-primary)' : 'white'}
                  />
                </Flex>

                <HStack spacing={2} mb={2}>
                  <Tag colorScheme="blue" size="sm">

                    {template?.message_type_id === 3 ? "Marketing" : "Informational"}
                  </Tag>
                  <Tag variant="outline" size="sm" colorScheme="gray">
                    ID: {template.id}
                  </Tag>
                </HStack>

                {/* An email template's `message` is a full HTML document, so printing
                    it here dumped "<!DOCTYPE html> <html xmlns=..." onto the card.
                    Strip tags for the summary line — the rendered version is available
                    from the preview further along the flow. */}
                <Text fontSize="xs" color="gray.600" mb={3} noOfLines={2}>
                  {toPlainSummary(template.message)}
                </Text>

                <Box mb={3}>
                  <HStack spacing={1} flexWrap="wrap">
                    {template?.message?.match(/\$[a-zA-Z_][a-zA-Z0-9_]*/g)?.map((variable, index) => (
                      <Tag key={index} variant="subtle" size="sm" colorScheme="gray">
                        {variable}
                      </Tag>
                    ))}
                  </HStack>
                </Box>

                <Divider mb={2} />

                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.500">
                    Created: {new Date(template.createdAt).toLocaleDateString()}
                  </Text>
                  {template.channel_id && (() => {
                    const ids = JSON.parse(template.channel_id);
                    const matched = CHANNELS.filter(c => ids.includes(c.id));
                    return matched.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap" mt={1}>
                        {matched.map(ch => (
                          <HStack
                            key={ch.id}
                            spacing={1}
                            px={2}
                            py={0.5}
                            borderRadius="full"
                            border="1px solid"
                            borderColor="gray.200"
                            bg="gray.50"
                          >
                            <Icon as={ch.icon} boxSize={3} color={ch.color} />
                            <Text fontSize="10px" color="gray.600">{ch.label}</Text>
                          </HStack>
                        ))}
                      </HStack>
                    ) : null;
                  })()}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      </Box>
      {pagination.total_events > 10 && (
        <Flex justify="center" mt={6}>
          <Box className="pagination-wrapper">
            <Pagination
              activePage={pagination.current_page}
              itemsCountPerPage={pagination.per_page}
              totalItemsCount={pagination.total_events}
              pageRangeDisplayed={5}
              onChange={handlePageChange}
              itemClass="page-item"
              linkClass="page-link"
              activeClass="active"
              activeLinkClass="active-link"
              disabledClass="disabled"
            />
          </Box>
        </Flex>
      )}

    </Box>
  );
};
